// @flow

import url from 'url';
import path from 'path';

import { createAction, createReducer } from 'redux-act';
import { createSelector } from 'reselect';

import type { FetchResponse } from '@gandi/redux-resource';
import type { TranslatorProps } from '@gandi/translate';
import { POST } from 'gandi.redux-fetcher';
import {
  actions as formActions,
  mapErrors,
  SubmissionError,
} from 'gandi.react-form';

import type {
  AbuseFormType,
  AbuseState,
  Dispatch,
  FullState,
  GetState,
} from '../../types';

export const sendStart: any = createAction('[ABUSE] Submit abuse form');
export const sendSuccess: any = createAction('[ABUSE] Abuse form submitted');
export const sendFail: any = createAction('[ABUSE] Abuse request failed');
export const setAbuseInitialValues: any = createAction(
  '[ABUSE] Abuse form initialization'
);
export const redirectStart: any = createAction(
  '[ABUSE] Redirect to success page'
);

type SelectRaw = (state: FullState) => AbuseState;

const selectRaw: SelectRaw = state => state.abuse;

export const selectAbuseInitialValues: (state: FullState) => mixed =
  createSelector(selectRaw, abuse => abuse.initialValues);

export const selectAbuseSuccess: (state: FullState) => boolean = createSelector(
  selectRaw,
  abuse => abuse.success
);
export const selectAbuseUpdating: (state: FullState) => boolean =
  createSelector(selectRaw, abuse => abuse.updating);

export function redirectOnSuccess(
  currentUrl: string,
  res: ?express$Response
): (dispatch: Dispatch) => Promise<mixed> {
  return (dispatch: Dispatch) => {
    if (currentUrl && res) {
      dispatch(redirectStart());
      const separator = currentUrl.indexOf('?') === -1 ? '?' : '&';
      const successUrl = `${currentUrl}${separator}success`;
      res.redirect(successUrl);
      return Promise.reject(new Error(`Redirect to ${successUrl}`));
    }
    return dispatch(sendSuccess());
  };
}

export function submitAbuseForm(
  values: AbuseFormType,
  routeParams: { +category: string, +topic: string, ... },
  __: TranslatorProps['__'],
  req: ?express$Request,
  res: ?express$Response,
  currentUrl: string = ''
): Promise<mixed> | ((dispatch: Dispatch, getState: GetState) => any) {
  // No-js category and topic choice
  if (('choose_subject' in values || 'choose_topic' in values) && req && res) {
    let pathname = routeParams.category
      ? req.path.replace(routeParams.category, values.category)
      : path.join(req.path, values.category);

    if (values.sub_category) {
      if (values.category !== routeParams.category) {
        // Reset sub-category (topic) when updating main category
        pathname = pathname.replace(routeParams.topic, '');
      } else {
        // Otherwise, let's update the topic
        pathname = routeParams.topic
          ? pathname.replace(routeParams.topic, values.sub_category)
          : path.join(pathname, values.sub_category);
      }
    }

    const redirTopicUrl = url.format({
      protocol: req.protocol,
      hostname: req.hostname,
      pathname,
    });

    res.redirect(redirTopicUrl);
    return Promise.reject(new Error(`Redirect to ${redirTopicUrl}`));
  }

  // Not changing the categories, let's submit the form
  return (dispatch: Dispatch, getState: GetState) => {
    dispatch(sendStart());

    const topics = {
      breaching: __('Copyright'),
      cybersquatting: __('Cybersquatting'),
      illegal: __('Illegal-Content'),
      malware: __('Malware'),
      other: __('Other'),
      pirated: __('Cyberattack'),
      phishing: __('Phishing'),
      spam: __('Spam'),
      whois: __('Whois'),
    };

    const params = {
      ...values,
      category: routeParams.category,
      sub_category: topics[routeParams.topic],
      lang: getState().locale,
    };

    return dispatch(
      POST(`/support/complaints`, params, {
        on2XX: () => dispatch(redirectOnSuccess(currentUrl, res)),
        on4XX: (response: FetchResponse<any, any>) => {
          const fieldErrors = mapErrors(response.data);

          // Server-side only:
          if (res) {
            dispatch(formActions.stopSubmit('AbuseForm', fieldErrors));
          }

          dispatch(sendFail());
          throw new SubmissionError(fieldErrors);
        },
        not2XX: (response: FetchResponse<>) => {
          if (!response.data || !response.data.errors) {
            return Promise.reject(dispatch(sendFail()));
          }
          return Promise.resolve(); // already handled in on4XX
        },
      })
    );
  };
}

const initialState = {
  initialValues: undefined,
  success: false,
  updating: false,
};

export default (createReducer(
  {
    [sendStart]: state => ({
      ...state,
      updating: true,
    }),
    [sendSuccess]: state => ({
      ...state,
      success: true,
      updating: false,
    }),
    [sendFail]: state => ({
      ...state,
      success: false,
      updating: false,
    }),
    [setAbuseInitialValues]: (state, payload) => ({
      ...state,
      initialValues: state.initialValues || payload,
    }),
  },
  initialState
): any);
